import { marked } from 'marked';
import type { Token, Tokens, TokensList } from 'marked';

import type { ChatMessage } from './AiChatContext';

/**
 * Heuristic: user is asking for customer-facing copy (draft email, reply, etc.).
 * Used to append My Escapes link when we have a specific order in context.
 */
export function isCustomerFacingDraftQuery(text: string | null | undefined): boolean {
  if (!text?.trim()) {
    return false;
  }
  const t = text.toLowerCase();
  const internalOnlyChecks = [
    /\bbug report\b/,
    /\bescalat(e|ion|ing)\b/,
    /\binternal\b/,
    /\bjira\b/,
    /\bincident\b/,
    /\bslack\b/,
  ];
  if (internalOnlyChecks.some((re) => re.test(t))) {
    return false;
  }
  const checks = [
    /\bdraft\b/,
    /\bemail\b/,
    /\breply\b/,
    /\brespond\b/,
    /\bresponse\b.*\bcustomer\b/,
    /\bcustomer[-\s]?facing\b/,
    /\bwrite\b[\s\S]{0,40}\bcustomer\b/,
    /\bwrite\b[\s\S]{0,40}\bguest\b/,
    /\bsuggest\b[\s\S]{0,40}\b(reply|response|email|wording)\b/,
    /\bmessage\b[\s\S]{0,40}\bcustomer\b/,
    /\bmessage\b[\s\S]{0,40}\bguest\b/,
    /\bcompose\b[\s\S]{0,30}\bemail\b/,
    /\bsend\b[\s\S]{0,30}\bcustomer\b/,
    /\bdraft\b[\s\S]{0,80}\bguest\b/,
    /\bguest\b[\s\S]{0,80}\bdraft\b/,
  ];
  return checks.some((re) => re.test(t));
}

export function isDepartmentSelectionQuestion(text: string | null | undefined): boolean {
  if (!text?.trim()) {
    return false;
  }

  const t = text.toLowerCase();
  const mentionsDepartment = t.includes('department');
  const asksForChoice =
    t.includes('which department are you in') ||
    t.includes('what department are you in') ||
    t.includes('please choose your department') ||
    t.includes('choose one only') ||
    t.includes('choose one') ||
    t.includes('select one');
  const includesKnownOptions =
    t.includes('sales') &&
    t.includes('customer support') &&
    t.includes('tours') &&
    t.includes('cruises') &&
    t.includes('flight support');

  return (mentionsDepartment && asksForChoice) || (mentionsDepartment && includesKnownOptions);
}

export function extractDepartmentSelection(text: string | null | undefined, options: Array<string>): string | null {
  if (!text?.trim()) {
    return null;
  }

  const normalized = text.trim().replace(/\.$/, '').toLowerCase();

  for (const option of options) {
    const optionNormalized = option.toLowerCase();
    if (normalized === optionNormalized) {
      return option;
    }
    if (
      normalized === `my department is ${optionNormalized}` ||
      normalized === `i am in ${optionNormalized}` ||
      normalized === `i'm in ${optionNormalized}`
    ) {
      return option;
    }
  }

  return null;
}

export function getDepartmentForAssistantMessage(
  messages: Array<ChatMessage>,
  assistantIndex: number,
  options: Array<string>,
  conversationDepartment?: string | null,
): string | null {
  for (let i = assistantIndex - 1; i >= 0; i--) {
    if (messages[i].role !== 'user') {
      continue;
    }

    const selectedDepartment = extractDepartmentSelection(messages[i].content, options);
    if (selectedDepartment) {
      return selectedDepartment;
    }
  }

  return conversationDepartment || null;
}

export function getPrecedingUserMessageContent(messages: Array<ChatMessage>, assistantIndex: number): string | null {
  for (let i = assistantIndex - 1; i >= 0; i--) {
    if (messages[i].role === 'user') {
      return messages[i].content;
    }
  }
  return null;
}

export function buildMyEscapesOrderUrl(customerPortalBase: string | null | undefined, orderId: string): string | null {
  if (!customerPortalBase || !orderId?.trim()) {
    return null;
  }
  const base = customerPortalBase.replace(/\/$/, '');
  return `${base}/account/my-escapes/order/${encodeURIComponent(orderId)}/`;
}

/** Avoid duplicating if the model already included this booking link. */
export function messageAlreadyContainsMyEscapesOrderLink(content: string, orderId: string): boolean {
  if (!content || !orderId) {
    return false;
  }
  const encoded = encodeURIComponent(orderId);
  return (
    content.includes(`/account/my-escapes/order/${orderId}/`) ||
    content.includes(`/account/my-escapes/order/${encoded}/`)
  );
}

function normalizePlainText(text: string): string {
  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function renderLinkText(label: string, href: string): string {
  const trimmedLabel = label.trim();
  const trimmedHref = href.trim();

  if (!trimmedHref) {
    return trimmedLabel;
  }

  if (!trimmedLabel || trimmedLabel === trimmedHref) {
    return trimmedHref;
  }

  return `${trimmedLabel} (${trimmedHref})`;
}

function getTokenText(token: Token): string {
  return 'text' in token && typeof token.text === 'string' ? token.text : '';
}

function getNestedTokens(token: Token): Array<Token> {
  return 'tokens' in token && Array.isArray(token.tokens) ? token.tokens : [];
}

function renderHtmlTokenText(text: string): string {
  const normalized = text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '- ')
    .replace(/<\/(p|div|section|article|blockquote|ul|ol|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '');

  if (normalized.trim()) {
    return normalized;
  }

  if (/^<\/?(br|sup|sub|em|strong|b|i|u|code|kbd|s|del|span|small|mark)\b[^>]*\/?>$/i.test(text.trim())) {
    return normalized;
  }

  return text;
}

function renderInlineToken(token: Token): string {
  switch (token.type) {
    case 'text': {
      const nestedTokens = getNestedTokens(token);
      return nestedTokens.length > 0 ? renderInlineTokens(nestedTokens) : token.text || '';
    }
    case 'strong':
    case 'em':
    case 'del':
      return token.tokens ? renderInlineTokens(token.tokens) : token.text || '';
    case 'codespan':
      return token.text || '';
    case 'br':
      return '\n';
    case 'link': {
      const label = token.tokens ? renderInlineTokens(token.tokens).trim() : token.text?.trim() || '';
      return renderLinkText(label, token.href || '');
    }
    case 'image':
      return token.text || token.href || '';
    case 'escape':
      return token.text || '';
    case 'html':
      return renderHtmlTokenText(token.text || '');
    default:
      return getNestedTokens(token).length > 0 ? renderInlineTokens(getNestedTokens(token)) : getTokenText(token);
  }
}

function renderInlineTokens(tokens: Array<Token> = []): string {
  return tokens.map(renderInlineToken).join('');
}

function indentMultilineText(text: string, prefix: string): string {
  const [firstLine = '', ...rest] = text.split('\n');
  const continuation = ' '.repeat(prefix.length);
  return [prefix + firstLine, ...rest.map((line) => `${continuation}${line.trimStart()}`)].join('\n');
}

function renderBlockToken(token: Token): string {
  switch (token.type) {
    case 'space':
    case 'hr':
      return '';
    case 'paragraph':
    case 'heading':
      return token.tokens ? renderInlineTokens(token.tokens) : token.text || '';
    case 'blockquote':
      return renderBlockTokens(token.tokens || []);
    case 'code':
      return token.text || '';
    case 'list': {
      const start = typeof token.start === 'number' ? token.start : 1;
      return token.items
        .map((item: Tokens.ListItem, index: number) => {
          const prefix = token.ordered ? `${start + index}. ` : '- ';
          const itemText = normalizePlainText(renderBlockTokens(item.tokens));
          return indentMultilineText(itemText, prefix);
        })
        .join('\n');
    }
    case 'table': {
      const header = token.header.map((cell: Tokens.TableCell) => renderInlineTokens(cell.tokens)).join(' | ');
      const rows = token.rows.map((row: Array<Tokens.TableCell>) =>
        row.map((cell: Tokens.TableCell) => renderInlineTokens(cell.tokens)).join(' | '),
      );
      return [header, ...rows].filter(Boolean).join('\n');
    }
    case 'text': {
      const nestedTokens = getNestedTokens(token);
      return nestedTokens.length > 0 ? renderInlineTokens(nestedTokens) : token.text || '';
    }
    default:
      if (getNestedTokens(token).length > 0) {
        return renderBlockTokens(getNestedTokens(token));
      }
      return getTokenText(token);
  }
}

function renderBlockTokens(tokens: Array<Token> | TokensList = []): string {
  return tokens
    .map((token) => renderBlockToken(token))
    .filter(Boolean)
    .join('\n\n');
}

function convertMarkdownToPlainText(content: string): string {
  return normalizePlainText(renderBlockTokens(marked.lexer(content)));
}

export function buildAssistantCopyText(assistantContent: string, myEscapesAppendix: string | null): string {
  const plainContent = convertMarkdownToPlainText(assistantContent);

  if (!myEscapesAppendix) {
    return plainContent;
  }
  return `${plainContent}\n\n${myEscapesAppendix}`;
}
